<script lang="ts">
  import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
  } from '$lib/components/ui/dialog';
  import {
    type PhilatelicSet,
    type Block,
    type Booklet,
    type SeTenants,
    PhilatelicItemType,
  } from '@briefmarken_katalog/types';
  import { t, locale } from '$lib/i18n';
  import { formatDateDisplay } from '$lib/utils/utils';
  import { translateLabel } from '$lib/philatelic-labels';
  import { typeIndicatorClass } from './typeColors';
  import { buildFacts, itemTypeKey, type CatalogDetailItem } from './catalogFacts';
  import ContainedStampsList from './ContainedStampsList.svelte';
  import PictureOverlay from '../PictureOverlay.svelte';
  import HelpButton from '../HelpButton.svelte';

  // dateOfIssue is supplied for a stamp of a set, which has no issue date of its own
  let { item, dateOfIssue, isOpen = $bindable(false) }: {
    item: CatalogDetailItem;
    dateOfIssue?: Date;
    isOpen: boolean;
  } = $props();

  // $locale is read so the facts re-translate on a language switch
  const facts = $derived(buildFacts(item, $locale));

  const issueDate = $derived(dateOfIssue ?? ('date_of_issue' in item ? item.date_of_issue : undefined));

  const pictures = $derived(item.pictures ?? []);
  const isSet = $derived(item.type === PhilatelicItemType.Set);
  const isCollection = $derived(
    item.type === PhilatelicItemType.Block ||
    item.type === PhilatelicItemType.Booklet ||
    item.type === PhilatelicItemType.SeTenants
  );

  let overlaySrc = $state<string | undefined>(undefined);
  let isOverlayOpen = $state(false);

  function openPicture(index: number) {
    const picture = pictures[index];
    overlaySrc = picture?.base64 || picture?.thumbnail;
    isOverlayOpen = true;
  }
</script>

<Dialog bind:open={isOpen}>
  <DialogContent class="sm:max-w-2xl flex flex-col overflow-hidden max-h-[90dvh]">
    <div class="flex items-center gap-2 pr-6">
      <span class="inline-block w-2.5 h-2.5 rounded-sm shrink-0 {typeIndicatorClass[item.type]}"></span>
      <DialogTitle class="min-w-0 break-words">{item.title}</DialogTitle>
      <HelpButton sectionId="item-details" />
    </div>
    <DialogDescription>
      {$t(itemTypeKey(item.type))}{#if issueDate} · {formatDateDisplay(new Date(issueDate), $locale)}{/if}
    </DialogDescription>

    <div class="overflow-y-auto flex-1 min-h-0 py-2">
      <!-- Picture and description form one block: the text flows around the image -->
      {#if pictures.length > 0}
        <button
          type="button"
          class="float-left mr-4 mb-2 card-3d card-3d-anim cursor-pointer"
          onclick={() => openPicture(0)}
          title={$t('alt.stamp')}
        >
          <img
            src={pictures[0].base64 ?? pictures[0].thumbnail}
            alt={$t('alt.stamp')}
            class="block w-32 sm:w-48 h-auto object-contain"
          />
        </button>
      {/if}

      {#if item.description}
        <p class="text-sm leading-relaxed whitespace-pre-line">{item.description}</p>
      {/if}

      <div class="clear-both"></div>

      {#if pictures.length > 1}
        <div class="mt-3 flex flex-wrap gap-2">
          {#each pictures.slice(1) as picture, i}
            <button
              type="button"
              class="card-3d card-3d-anim cursor-pointer"
              onclick={() => openPicture(i + 1)}
              title={$t('alt.stamp')}
            >
              <img
                src={picture.thumbnail ?? picture.base64}
                alt={$t('alt.stamp')}
                class="block h-20 w-auto object-contain"
              />
            </button>
          {/each}
        </div>
      {/if}

      {#if facts.length > 0}
        <div class="mt-4 flex flex-wrap gap-1.5">
          {#each facts as fact}
            <span class="inline-flex items-baseline gap-1.5 rounded-full border bg-secondary/50 px-2.5 py-1 text-xs">
              <span class="text-muted-foreground">{fact.label}</span>
              <span class="font-semibold">{fact.value}</span>
            </span>
          {/each}
        </div>
      {/if}

      {#if item.categories && item.categories.length > 0}
        <div class="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {#each item.categories as category}
            <span>#{translateLabel(category)}</span>
          {/each}
        </div>
      {/if}

      {#if isSet || isCollection}
        <div class="mt-4">
          <div class="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {$t('editor.stamps')}
          </div>
          {#if isSet}
            <ContainedStampsList
              stamps={(item as PhilatelicSet).stamps ?? []}
              dateOfIssue={issueDate ? new Date(issueDate) : undefined}
            />
          {:else}
            <ContainedStampsList
              stampIds={((item as Block | Booklet | SeTenants).stamps ?? []).map((id) => String(id))}
            />
          {/if}
        </div>
      {/if}
    </div>
  </DialogContent>
</Dialog>

<PictureOverlay src={overlaySrc} alt={$t('alt.aStamp')} bind:open={isOverlayOpen} />
